import React from 'react';
import { useWorkViewContext, Provider as WorkViewProvider } from './Provider';
import { IDs } from '../../Data/Misc';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../Components/Icons/Icon';
import Title from '../../Components/Formatting/Customer/Title';
import Subtitle from '../../Components/Formatting/Customer/Subtitle';
import Services from '../../Components/Formatting/Services';
import Money from '../../Components/Formatting/Money';
import Dropdown from '../../Components/Dropdown';
import { getEntityLink, useDirectionsTo } from '../../Services/helpers';
import { useOffline } from '../../Components/Offline';
import { Roles, hasRole } from '../../Data/Users';
import Tile from '../../Components/Containers/Tile';

const WorkWidget = () => {
    const date = new Date().toISOString().split('T')[0];

    return <WorkViewProvider date={date} filterOverride={{assignedTo: IDs.ONE, roundId: null, hideCompleted: true}}>
        <Inner />
    </WorkViewProvider>;
};

const Inner = () => {
    const {filteredWithoutCompleted} = useWorkViewContext();

    if (filteredWithoutCompleted.length === 0) {
        return <Tile title="Next up"
            icon="CircleArrowRight"
            options={<Link to="/work">Work view <Icon icon="RightChevron" size="0.5x" /></Link>}
        >
            <div className="text-center text-sm text-muted my-4">No more jobs today 🎉</div>
        </Tile>
    }

    return <Tile title="Next up"
            icon="CircleArrowRight"
            options={<Link to="/work">Work view <Icon icon="RightChevron" size="0.5x" /></Link>}
            flush
        >
            <div className="flex flex-col gap-2">
            {filteredWithoutCompleted.slice(0, 2).map(j => <Job key={j.id} job={j} />)}
        </div>
    </Tile>;
};

const Job = ({job}) => {
    const {setMarkingJobDone, setSkipping, setScheduling} = useWorkViewContext();
    const directionsTo = useDirectionsTo();
    const {offlineSupported} = useOffline();
    const navigate = useNavigate();

    return <div className="flex flex-row items-top justify-between p-2 surface-1 rounded-lg surface-outline">
        <div>
            <Title customer={job.customer} addressSegments={2} />
            <br />
            <Subtitle customer={job.customer} className="text-muted text-sm" /><br />
            <Services services={job.services} className="text-muted text-sm" /><br />
        </div>
        <div className="flex flex-col items-end justify-between">
            <Dropdown options={[
                {
                    title: 'Mark as done',
                    icon: 'Check',
                    onClick: () => setMarkingJobDone(job),
                    availableOffline: offlineSupported,
                },
                {
                    title: 'Directions',
                    icon: 'Route',
                    onClick: () => directionsTo(job.customer.address),
                },
                {
                    div: true,
                },
                {
                    title: 'Skip job',
                    icon: 'Forward',
                    onClick: () => setSkipping(job),
                },
                {
                    title: 'Reschedule job',
                    icon: 'CalendarPlus',
                    onClick: () => setScheduling(job),
                    role: Roles.Manager,
                },
                {
                    div: true,
                },
                {
                    title: 'View customer',
                    icon: 'User',
                    onClick: () => navigate(getEntityLink('Customers/Customer', job.customer.id)),
                    role: Roles.Manager,
                },
                {
                    title: 'View quote',
                    icon: 'ClipboardCheck',
                    onClick: () => navigate(getEntityLink('Quotes/Quote', job.quoteId)),
                    role: Roles.Manager,
                    hidden: !job.quoteId,
                }
            ]} />
            {hasRole(Roles.Worker) && <Money pence={job.cost + (job?.itemisationAdjustment?.amount ?? 0)}
                className="text-brand"
            />}
        </div>
    </div>
}

export default WorkWidget;
